import {
  Box,
  Drawer,
  Typography,
  Grid,
  Card,
  CardActionArea,
  TextField,
  useTheme,
  IconButton,
  Link as MUILink
} from '@mui/material';
import { IoClose } from 'react-icons/io5';
import Input from 'components/Common/Input';
import { FormProvider, useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateIntegration,
  useCreateOauthIntegration,
  useGetAvailableIntegrations,
  useGetIntegration,
  useGetPostConfigurationFields,
  useUpdateIntegration
} from 'apis/business/setting/integrations/hook';
import AddIntegrationFormSkeleton from './AddIntegrationFormSkeleton';
import FormSchemaParser from 'components/Common/FormSchemaParser';
import { useState, useMemo, useEffect } from 'react';
import { EmptyStateView } from 'components/Common/EmptyStateView';
import ReactMarkdown from 'react-markdown';
import { useGetLocations } from '@/apis/business/setting/location/hook';
import Select from '@/components/Common/Select';
import * as urls from 'routes/config';
import { queryClient } from '@/providers/AllProviders';
import { FaRegEdit } from 'react-icons/fa';
import CharmHealthForm from '../FormPartials/CharmHealthForm';
import IntegrationPostConfigurationFields from './IntegrationPostConfigurationFields';
import StreamOrderSettings from './StreamOrderSettings';
import VendorInitiatedForm from '../FormPartials/VendorInitiatedForm';
import ModeToggleField from '../FormPartials/ModeToggleField';
import IntegrationCredentialModal from './IntegrationCredentialModal';
import { getApiErrorMessage } from '@/apis/business/setting/integrations/api';

function normalizeConfigurationNumericFields(data: any) {
  if (!data?.configuration) return data;
  const c = data.configuration;
  const pt = c.prep_time_minutes;
  const ps = c.payment_setting;
  return {
    ...data,
    configuration: {
      ...c,
      prep_time_minutes: pt === '' || pt === undefined || pt === null ? 0 : Number(pt) || 0,
      payment_setting: ps
        ? {
            ...ps,
            part_value:
              ps.part_value === '' || ps.part_value === undefined || ps.part_value === null
                ? 0
                : Number(ps.part_value) || 0
          }
        : ps
    }
  };
}

export default function AddIntegrationForm({ isOpen, handleClose }: { isOpen?: boolean; handleClose?: any }) {
  const { integrationId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [editForm, setEditForm] = useState(integrationId ? false : true);

  const { mutateAsync: createIntegration, isPending: creatingIntegration } = useCreateIntegration();
  const { mutateAsync: updateIntegration, isPending: updatingIntegration } = useUpdateIntegration();
  const { mutateAsync: handleCreateOauthIntegration } = useCreateOauthIntegration();
  const { data: IntegrationData, isLoading: isIntegrationDataLoading } = useGetIntegration(Number(integrationId), {
    include: 'locations'
  });
  const { data: locations, isLoading: isLocationsLoading } = useGetLocations();
  const { data: availableIntegrations, isLoading: isAvailableIntegrationLoading } = useGetAvailableIntegrations({
    per_page: 9999
  });

  const methods = useForm({
    values: { ...IntegrationData, location_ids: IntegrationData?.locations.map((location: any) => location.id) }
  });
  const { handleSubmit, control, watch, setValue, getValues, reset } = methods;
  const formData = watch();
  const selectedIntegrationCode = watch('code');
  const selectedLocations = watch('location_ids');
  const selectedSingleLocation = watch('location_ids.0');
  const enteredIntegrationName = watch('name');
  const configuration = watch('configuration');
  const selectedIntegrationData = availableIntegrations?.data?.find(
    (item: any) => item.code === selectedIntegrationCode
  );
  const hasSelectedLocation = selectedIntegrationData?.multi_location_support
    ? Array.isArray(selectedLocations) && selectedLocations.some((id) => id != null && id !== '')
    : selectedSingleLocation != null && selectedSingleLocation !== '';
  const hasRequiredPreConfigFields =
    selectedIntegrationData?.pre_configurations
      ?.filter((f: any) => f.required)
      .every((f: any) => {
        const v = configuration?.[f.key];
        if (f.type === 'boolean') return v != null;
        if (f.multiple) return Array.isArray(v) && v.length > 0;
        return v != null && v !== '';
      }) ?? true;
  const isFormFilled = Boolean(
    selectedIntegrationCode && hasSelectedLocation && enteredIntegrationName?.trim() && hasRequiredPreConfigFields
  );

  const isJaneApp = selectedIntegrationData?.code === 'jane_app';

  const { data: postConfigurationFields, isLoading: fetchingPostConfigFields } = useGetPostConfigurationFields(
    integrationId,
    selectedIntegrationData?.post_configuration_required && editForm
  );

  const memorizedPostConfigFields = useMemo(() => {
    return editForm ? postConfigurationFields : IntegrationData?.configuration?.post_configurations;
  }, [editForm, postConfigurationFields, IntegrationData?.configuration?.post_configurations]);

  useEffect(() => {
    setValue('configuration.post_configurations', postConfigurationFields);
    if (postConfigurationFields && integrationId) {
      queryClient.invalidateQueries({ queryKey: ['integration', Number(integrationId)] });
    }
  }, [postConfigurationFields, setValue, integrationId]);

  const [search, setSearch] = useState('');
  const [credentialModalStatus, setCredentialModalStatus] = useState<null | 'validating' | 'success' | 'error'>(null);
  const [createdIntegrationId, setCreatedIntegrationId] = useState<number | null>(null);
  const [credentialErrorMessage, setCredentialErrorMessage] = useState('');

  const filteredIntegrations = useMemo(() => {
    if (!availableIntegrations?.data) return [];
    return availableIntegrations.data.filter((integration: any) =>
      integration.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, availableIntegrations]);

  const handleAuthentication = () => {
    handleCreateOauthIntegration({
      code: selectedIntegrationCode,
      name: formData.name,
      location_ids: formData.location_ids,
      id: integrationId,
      configuration
    });
  };

  const onSubmit = async (data: any, source?: string) => {
    const normalized = normalizeConfigurationNumericFields(data);
    const payload = {
      integration: { ...normalized, integration_name: selectedIntegrationData?.integration_name }
    };

    if (integrationId) {
      await updateIntegration(payload);
    } else {
      setCredentialModalStatus('validating');
      setCredentialErrorMessage('');
      try {
        const response = await createIntegration(payload);
        const id = response?.id ?? response?.data?.id;
        const isActive = response?.active === true;

        if (isActive && id) {
          setCreatedIntegrationId(id);
          setCredentialModalStatus('success');
          return;
        }

        setCredentialErrorMessage('Credential validation failed.');
        setCredentialModalStatus('error');
        return;
      } catch (error) {
        setCredentialErrorMessage(getApiErrorMessage(error));
        setCredentialModalStatus('error');
        return;
      }
    }

    if (source === 'save') {
      handleClose ? handleClose() : navigate(-1);
      reset();
    } else {
      queryClient.invalidateQueries({
        queryKey: ['post_configs']
      });
    }
  };

  const handleCredentialModalClose = () => {
    if (credentialModalStatus === 'success' && createdIntegrationId) {
      const integrationPath = `${urls.business.base}/${urls.business.integration}/${createdIntegrationId}`;
      setCredentialModalStatus(null);
      setCreatedIntegrationId(null);
      reset();
      handleClose ? handleClose() : navigate(integrationPath);
      return;
    }
    setCredentialModalStatus(null);
  };

  const handleCredentialEdit = () => {
    setCredentialModalStatus(null);
  };

  const handleCredentialRetry = async () => {
    setCredentialModalStatus(null);
    await handleSubmit((data) => onSubmit(data, 'save'))();
  };

  const handleAutoSubmit = async (val: any) => {
    setValue(val.target.name, val.target.value);
    await handleSubmit((data) => onSubmit(data, 'form_parser'))();
  };

  if (isIntegrationDataLoading || isAvailableIntegrationLoading) {
    return <AddIntegrationFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open={isOpen === undefined ? true : isOpen}
      onClose={() => (handleClose ? handleClose() : navigate(-1))}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 500, md: 800 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          backgroundColor: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 2
        }}
      >
        <Box display='flex' flexDirection='row' alignItems='center' gap={0.5}>
          <Typography variant='h6'>{integrationId ? 'Configure Integration' : 'Add Integration'}</Typography>
          {!editForm && !selectedIntegrationData?.vendor_initiated_onboarding && (
            <IconButton onClick={() => setEditForm(true)} size='small'>
              <FaRegEdit size={17} color={theme.palette.primary.main} />
            </IconButton>
          )}
        </Box>
        <IconButton onClick={() => (handleClose ? handleClose() : navigate(-1))} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <FormProvider {...methods}>
        <Box
          component='form'
          onSubmit={handleSubmit((data) => onSubmit(data, 'save'))}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 3
            }}
          >
            {/* --- Selected Integration Banner --- */}
            {selectedIntegrationData && (
              <Card
                sx={{
                  border: '1px solid #ececec',
                  borderRadius: 2,
                  mb: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.light,
                  boxShadow: 'none'
                }}
              >
                <img
                  src={selectedIntegrationData.small_avatar_url}
                  alt={selectedIntegrationData.name}
                  style={{ width: 36, height: 36, objectFit: 'contain' }}
                />
                <Box>
                  <Box display='flex' flexDirection='row' justifyContent='space-between'>
                    <Typography variant='subtitle1' fontWeight={600}>
                      {selectedIntegrationData.name}
                    </Typography>
                    {!integrationId && (
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => {
                          setValue('code', '');
                          setSearch('');
                        }}
                      >
                        Change Integration
                      </Typography>
                    )}
                  </Box>
                  <Typography variant='body2' color='text.secondary'>
                    <ReactMarkdown
                      components={{
                        a: ({ ...props }) => (
                          <MUILink
                            {...(props as any)}
                            component='a'
                            target='_blank'
                            rel='noopener'
                            sx={{ color: 'primary.main', fontWeight: 500 }}
                          />
                        ),
                        p: ({ ...props }) => <span {...props} />
                      }}
                    >
                      {selectedIntegrationData?.description}
                    </ReactMarkdown>
                  </Typography>
                </Box>
              </Card>
            )}

            {/* --- Integration Grid Selection --- */}
            {!selectedIntegrationData && (
              <>
                <TextField
                  fullWidth
                  placeholder='Search integrations...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ececec'
                      }
                    },
                    mb: 2
                  }}
                />

                <Grid
                  container
                  spacing={2}
                  sx={{
                    my: 0.5,
                    pb: 1,
                    pr: 2,
                    alignContent: 'flex-start'
                  }}
                >
                  {filteredIntegrations?.length ? (
                    filteredIntegrations.map((integration: any) => (
                      <Grid item xs={12} sm={6} md={4} key={integration.code}>
                        <Card
                          sx={{
                            border:
                              integration.code === selectedIntegrationCode ? '2px solid #1976d2' : '1px solid #ececec',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: '0.2s',
                            boxShadow: 'none',
                            '&:hover': { border: `1px solid ${theme.palette.primary.main}` }
                          }}
                        >
                          <CardActionArea onClick={() => setValue('code', integration.code)}>
                            <Box p={2.5} display='flex' alignItems='center' gap={1.5}>
                              <img
                                src={integration.small_avatar_url}
                                alt={integration.name}
                                style={{
                                  width: 28,
                                  height: 28,
                                  objectFit: 'contain'
                                }}
                              />
                              <Typography variant='body2' fontWeight={600} noWrap>
                                {integration.name}
                              </Typography>
                            </Box>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Box display='flex' justifyContent='center' alignItems='center' mx='auto'>
                      <EmptyStateView />
                    </Box>
                  )}
                </Grid>
              </>
            )}

            {/* --- Config Form after selection --- */}
            {selectedIntegrationData ? (
              selectedIntegrationData?.vendor_initiated_onboarding && !integrationId ? (
                <VendorInitiatedForm />
              ) : (
                <Box mt={3}>
                  {selectedIntegrationData?.connection_type === 'oauth' ||
                  selectedIntegrationData?.pre_configurations.length ? (
                    <>
                      <Input
                        name='name'
                        label='Name'
                        placeholder='Enter a friendly name for this integration'
                        control={control}
                        disabled={!editForm}
                        fullWidth
                        required
                      />
                      <Box mt={3}>
                        <Select
                          name={selectedIntegrationData?.multi_location_support ? 'location_ids' : 'location_ids[0]'}
                          disabled={isLocationsLoading || !editForm}
                          control={control}
                          label='Select Locations'
                          multiple={selectedIntegrationData?.multi_location_support}
                          required
                          options={
                            locations?.data?.map((location) => ({ label: location.name, value: location.id })) ?? []
                          }
                        />
                      </Box>
                    </>
                  ) : (
                    ''
                  )}

                  <>
                    {selectedIntegrationData?.connection_type === 'credentials' &&
                      [
                        'boulevard',
                        'meevo',
                        'itrust',
                        'dentrix_ascend',
                        'akitu_one',
                        'athena_health',
                        'nex_health'
                      ].includes(selectedIntegrationData?.code) && (
                        <ModeToggleField namePrefix='configuration' control={control} disabled={!editForm} />
                      )}
                    {selectedIntegrationData?.pre_configurations && (
                      <Box mt={2}>
                        <FormSchemaParser
                          disabled={!editForm}
                          fields={selectedIntegrationData.pre_configurations || []}
                          control={control}
                          namePrefix='configuration'
                          gridSize={12}
                          spacing='16px'
                        />
                      </Box>
                    )}
                    {selectedIntegrationCode === 'rpower' && integrationId && (
                      <Box mt={selectedIntegrationData?.pre_configurations?.length ? 3 : 0}>
                        <StreamOrderSettings />
                      </Box>
                    )}
                    {selectedIntegrationData.connection_type === 'custom' &&
                      selectedIntegrationCode === 'charm_health' && (
                        <CharmHealthForm
                          namePrefix='configuration'
                          control={control}
                          disabled={!editForm}
                          isActive={Boolean(IntegrationData?.active)}
                        />
                      )}
                  </>

                  {selectedIntegrationData?.connection_type === 'oauth' && (
                    <Box mt={3}>
                      <Typography fontWeight={500}>OAuth2 Authentication</Typography>
                      <Typography color='secondary' fontSize={14} mb={2}>
                        {selectedIntegrationData.name} requires OAuth2 authentication.
                      </Typography>
                      <Button
                        sx={{
                          opacity: IntegrationData?.active ? '50%' : '100%',
                          bgcolor: isFormFilled ? theme.palette.primary.main : theme.palette.secondary.main
                        }}
                        disabled={!isFormFilled || IntegrationData?.active}
                        onClick={handleAuthentication}
                      >
                        {IntegrationData?.active
                          ? `Authenticated with ${selectedIntegrationData.name}`
                          : `Authenticate with ${selectedIntegrationData.name}`}
                      </Button>
                    </Box>
                  )}

                  {integrationId && selectedIntegrationData?.post_configuration_required && IntegrationData?.active && (
                    <Box mt={2}>
                      <IntegrationPostConfigurationFields
                        selectedCode={selectedIntegrationCode}
                        integrationId={integrationId}
                        configFields={memorizedPostConfigFields}
                        namePrefix='configuration'
                        control={control}
                        disabled={!editForm}
                        isLoading={fetchingPostConfigFields}
                        getValues={getValues}
                        setValue={setValue}
                        submitForm={(val: any) => handleAutoSubmit(val)}
                      />
                    </Box>
                  )}
                </Box>
              )
            ) : (
              ''
            )}
          </Box>

          {!selectedIntegrationData?.vendor_initiated_onboarding && (
            <Box
              sx={{
                p: 3,
                borderTop: '1px solid #DEDEDE',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                flexShrink: 0,
                zIndex: 999,
                backgroundColor: '#fff',
                position: 'sticky',
                bottom: 0
              }}
            >
              <Button color='secondary' variant='outlined' onClick={() => (handleClose ? handleClose() : navigate(-1))}>
                Cancel
              </Button>
              {(selectedIntegrationData?.connection_type !== 'oauth' || IntegrationData?.active) && editForm && (
                <Button
                  type='submit'
                  disabled={!isFormFilled}
                  variant='contained'
                  isPending={updatingIntegration || creatingIntegration}
                  loadingText={isJaneApp ? 'Validating Credentials' : ''}
                >
                  Save
                </Button>
              )}
            </Box>
          )}
        </Box>
      </FormProvider>

      <IntegrationCredentialModal
        open={credentialModalStatus !== null}
        status={credentialModalStatus ?? 'validating'}
        integrationName={selectedIntegrationData?.name ?? 'integration'}
        errorMessage={credentialErrorMessage}
        disableClose={credentialModalStatus === 'validating'}
        onClose={handleCredentialModalClose}
        onEditCredentials={handleCredentialEdit}
        onRetry={handleCredentialRetry}
      />
    </Drawer>
  );
}
